import { useMemo } from 'react';

import { SortableColumnsConfig } from '@/components/table/sortableColumns/types';
import { Column } from '@/components/table/types';
import { StorageKey, useStorage } from '@/utils/storage/useStorage.util';

export const useSortableColumn = <TData>({
  key,
  defaultColumns,
  defaultConfig,
}: {
  key: string;
  defaultColumns: Column<TData>[];
  defaultConfig: SortableColumnsConfig;
}) => {
  const [activeColumns, setActiveColumns] = useStorage<string[] | undefined>(
    {
      key: `${StorageKey.SORTABLE_COLUMNS}_${key}`,
    },
    defaultConfig.active.map(column => column.id)
  );

  const columns = useMemo(() => {
    const activeColumnIds = activeColumns ?? [];
    const fixedColumnIds = defaultConfig.fixed.map(config => config.id);
    const fixedLastColumnIds = defaultConfig.fixedLast
      ? defaultConfig.fixedLast.map(config => config.id)
      : [];

    // First, get all columns that should be included (fixed + active + fixedLast)
    const allIncludedIds = new Set([
      ...fixedColumnIds,
      ...activeColumnIds,
      ...fixedLastColumnIds,
    ]);

    // Preserve the order from activeColumns, but add fixed columns if they're not in activeColumns
    const orderedIds: string[] = [];

    // Add fixed columns first (always at the start)
    for (const id of fixedColumnIds) {
      if (!orderedIds.includes(id)) {
        orderedIds.push(id);
      }
    }

    // Add active columns in their specified order
    for (const id of activeColumnIds) {
      if (allIncludedIds.has(id) && !fixedLastColumnIds.includes(id)) {
        orderedIds.push(id);
      }
    }

    // Add fixedLast columns at the end (always last)
    for (const id of fixedLastColumnIds) {
      if (!orderedIds.includes(id)) {
        orderedIds.push(id);
      }
    }

    return orderedIds
      .map(id => defaultColumns.find(column => column.id === id))
      .filter(column => column !== undefined);
  }, [activeColumns, defaultConfig, defaultColumns]);

  return {
    activeColumns,
    setActiveColumns,
    columns,
  };
};
